import { DiaSemana, DiasHorario, IFichajeAnulado, IJornadaEvento, ITramo, NombresCorrecciones, TipoFichaje } from "../types/Jornada";

// Horarios del registro de jornada: resumen, total semanal y validación (la misma regla que la API).

export const DIAS_SEMANA: DiaSemana[] = ['1', '2', '3', '4', '5', '6', '7'];
export const LABORABLES: DiaSemana[] = ['1', '2', '3', '4', '5'];
export const NOMBRE_DIA: Record<DiaSemana, string> = { '1': 'Lunes', '2': 'Martes', '3': 'Miércoles', '4': 'Jueves', '5': 'Viernes', '6': 'Sábado', '7': 'Domingo' };
const INICIAL_DIA: Record<DiaSemana, string> = { '1': 'L', '2': 'M', '3': 'X', '4': 'J', '5': 'V', '6': 'S', '7': 'D' };
const FORMATO_HORA = /^([01]\d|2[0-3]):[0-5]\d$/;

const minutos = (hora: string) => Number(hora.slice(0, 2)) * 60 + Number(hora.slice(3, 5));
// '09:00' → '9:00'
const horaCorta = (hora: string) => hora.replace(/^0(\d)/, '$1');
const textoTramos = (tramos: ITramo[]) => tramos.map(t => `${horaCorta(t.inicio)}–${horaCorta(t.fin)}`).join(' · ');

// «L–V 9:00–14:00 · 16:00–20:00, S 9:00–13:00»: agrupa los días seguidos con los mismos tramos
export const resumenHorario = (dias: DiasHorario | null | undefined): string => {
    if (!dias) return 'Sin horario';
    const grupos: { desde: DiaSemana; hasta: DiaSemana; texto: string }[] = [];
    for (const dia of DIAS_SEMANA) {
        const tramos = dias[dia];
        if (!tramos || tramos.length === 0) continue;
        const texto = textoTramos(tramos);
        const ultimo = grupos[grupos.length - 1];
        if (ultimo && ultimo.texto === texto && Number(dia) === Number(ultimo.hasta) + 1) ultimo.hasta = dia;
        else grupos.push({ desde: dia, hasta: dia, texto });
    }
    if (grupos.length === 0) return 'Sin horario';
    return grupos.map(g => `${INICIAL_DIA[g.desde]}${g.desde !== g.hasta ? `–${INICIAL_DIA[g.hasta]}` : ''} ${g.texto}`).join(', ');
};

// «38 h/semana», «37 h 30 min/semana»; solo cuenta los tramos bien escritos
export const totalSemanal = (dias: DiasHorario): string => {
    const total = DIAS_SEMANA.reduce((suma, dia) => suma + (dias[dia] ?? [])
        .filter(t => FORMATO_HORA.test(t.inicio) && FORMATO_HORA.test(t.fin) && t.fin > t.inicio)
        .reduce((s, t) => s + minutos(t.fin) - minutos(t.inicio), 0), 0);
    const horas = Math.floor(total / 60);
    const resto = total % 60;
    return `${horas} h${resto ? ` ${resto} min` : ''}/semana`;
};

// Errores por tramo, con la clave «día-índice»
export const erroresTramos = (dias: DiasHorario): Record<string, string> => {
    const errores: Record<string, string> = {};
    for (const dia of DIAS_SEMANA) {
        const tramos = dias[dia] ?? [];
        tramos.forEach((t, i) => {
            if (!FORMATO_HORA.test(t.inicio) || !FORMATO_HORA.test(t.fin)) errores[`${dia}-${i}`] = 'Indica la hora de inicio y de fin';
            else if (t.fin <= t.inicio) errores[`${dia}-${i}`] = 'La hora de fin debe ser posterior a la de inicio';
        });
        const ordenados = tramos.map((t, i) => ({ t, i })).filter(({ i }) => !errores[`${dia}-${i}`]).sort((a, b) => a.t.inicio.localeCompare(b.t.inicio));
        for (let k = 1; k < ordenados.length; k++) {
            if (ordenados[k].t.inicio < ordenados[k - 1].t.fin) errores[`${dia}-${ordenados[k].i}`] = 'Este tramo se solapa con otro del mismo día';
        }
    }
    return errores;
};

// '2026-10-06' → '6 de octubre' / '6 oct'; las fechas del horario son días, sin hora ni zona
const comoFecha = (fecha: string) => new Date(Number(fecha.slice(0, 4)), Number(fecha.slice(5, 7)) - 1, Number(fecha.slice(8, 10)));
export const fechaLarga = (fecha: string) => comoFecha(fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
export const fechaCorta = (fecha: string) => comoFecha(fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });

// Versión vigente en una fecha: la de mayor desde que no la supera (igual que en la API)
export const horarioVigente = <T extends { desde: string }>(versiones: T[], fecha: string): T | null =>
    versiones.filter(v => v.desde <= fecha).reduce<T | null>((vigente, v) => (!vigente || v.desde > vigente.desde ? v : vigente), null);

// Tramos de un día en minutos desde las 00:00 ([{ inicio: 540, fin: 840 }]) según la versión del horario vigente ese día.
// null si ese día aún no había horario (entonces no se sombrea nada); [] si el horario no tiene ese día (todo fuera).
export const tramosEnMinutos = (versiones: { desde: string; dias: DiasHorario }[], dia: Date): { inicio: number; fin: number }[] | null => {
    const fecha = `${dia.getFullYear()}-${String(dia.getMonth() + 1).padStart(2, '0')}-${String(dia.getDate()).padStart(2, '0')}`;
    const vigente = horarioVigente(versiones, fecha);
    if (!vigente) return null;
    const diaSemana = String(((dia.getDay() + 6) % 7) + 1) as DiaSemana;
    return (vigente.dias[diaSemana] ?? []).map(t => ({ inicio: minutos(t.inicio), fin: minutos(t.fin) }));
};

// ---------- No disponibles (solo lectura en Registro de jornada) ----------

const soloDia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const horaMinutos = (d: Date) => d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
const nombreDiaSemana = (d: Date) => d.toLocaleDateString('es-ES', { weekday: 'long' });
const mesCorto = (d: Date) => d.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '');
const conAnio = (d: Date) => (d.getFullYear() !== new Date().getFullYear() ? ` ${d.getFullYear()}` : '');
const mayuscula = (texto: string) => texto.charAt(0).toUpperCase() + texto.slice(1);

const duracion = (minutosTotales: number) => {
    const horas = Math.floor(minutosTotales / 60);
    const resto = minutosTotales % 60;
    if (!horas) return `${resto} min`;
    return `${horas} h${resto ? ` ${resto} min` : ''}`;
};

// «Martes 10 nov · 10:00–14:00 (4 h)», «Martes 10 nov · todo el día», «Del lunes 9 al miércoles 11 nov · 3 días» o
// «Del lunes 9 nov 10:00 al miércoles 11 nov 14:00 · 3 días». Las horas, en la del navegador, como en la Agenda.
export const textoNoDisponible = (inicio: string | Date, fin: string | Date, ahora: Date = new Date()): { texto: string; enCurso: boolean } => {
    const s = new Date(inicio);
    const e = new Date(fin);
    const enCurso = s.getTime() <= ahora.getTime() && ahora.getTime() < e.getTime();

    // Todo el día: empieza a las 00:00 y acaba a las 23:59 o a las 00:00 del día siguiente
    const empiezaAlInicio = s.getHours() === 0 && s.getMinutes() === 0;
    const acabaAMedianoche = e.getHours() === 0 && e.getMinutes() === 0 && e.getTime() > s.getTime();
    const acabaAlFinal = (e.getHours() === 23 && e.getMinutes() === 59) || acabaAMedianoche;
    const diasCompletos = empiezaAlInicio && acabaAlFinal;
    // Si acaba a las 00:00, su último día es el anterior, empiece a la hora que empiece
    const ultimoDia = acabaAMedianoche ? new Date(soloDia(e).getTime() - 24 * 60 * 60 * 1000) : soloDia(e);
    const mismoDia = soloDia(s).getTime() === ultimoDia.getTime();

    let texto: string;
    if (mismoDia) {
        const dia = `${mayuscula(nombreDiaSemana(s))} ${s.getDate()} ${mesCorto(s)}${conAnio(s)}`;
        texto = diasCompletos
            ? `${dia} · todo el día`
            : `${dia} · ${horaMinutos(s)}–${horaMinutos(e)} (${duracion(Math.floor(e.getTime() / 60000) - Math.floor(s.getTime() / 60000))})`;
    } else if (diasCompletos) {
        const numDias = Math.round((ultimoDia.getTime() - soloDia(s).getTime()) / (24 * 60 * 60 * 1000)) + 1;
        const mismoMes = s.getMonth() === ultimoDia.getMonth() && s.getFullYear() === ultimoDia.getFullYear();
        const desde = `${nombreDiaSemana(s)} ${s.getDate()}${mismoMes ? '' : ` ${mesCorto(s)}${conAnio(s)}`}`;
        const hasta = `${nombreDiaSemana(ultimoDia)} ${ultimoDia.getDate()} ${mesCorto(ultimoDia)}${conAnio(ultimoDia)}`;
        texto = `Del ${desde} al ${hasta} · ${numDias} días`;
    } else {
        // Días naturales que toca, del primero al último, ambos incluidos
        const numDias = Math.round((ultimoDia.getTime() - soloDia(s).getTime()) / (24 * 60 * 60 * 1000)) + 1;
        texto = `Del ${nombreDiaSemana(s)} ${s.getDate()} ${mesCorto(s)}${conAnio(s)} ${horaMinutos(s)} al ${nombreDiaSemana(e)} ${e.getDate()} ${mesCorto(e)}${conAnio(e)} ${horaMinutos(e)} · ${numDias} días`;
    }
    return { texto, enCurso };
};

// Horario por líneas para la fila de cada persona: una por grupo de días con los mismos tramos, aunque no vayan seguidos
// ({ dias: 'L, X', horas: '16:00–21:00' }). Los días seguidos se escriben como rango («L–V»).
export const lineasHorario = (dias: DiasHorario | null | undefined): { dias: string; horas: string }[] => {
    if (!dias) return [];
    const grupos: { dias: DiaSemana[]; horas: string }[] = [];
    for (const dia of DIAS_SEMANA) {
        const tramos = dias[dia];
        if (!tramos || tramos.length === 0) continue;
        const horas = textoTramos(tramos);
        const grupo = grupos.find(g => g.horas === horas);
        if (grupo) grupo.dias.push(dia);
        else grupos.push({ dias: [dia], horas });
    }
    const textoDias = (lista: DiaSemana[]) => {
        const tramos: DiaSemana[][] = [];
        for (const dia of lista) {
            const ultimo = tramos[tramos.length - 1];
            if (ultimo && Number(dia) === Number(ultimo[ultimo.length - 1]) + 1) ultimo.push(dia);
            else tramos.push([dia]);
        }
        return tramos.map(t => (t.length > 1 ? `${INICIAL_DIA[t[0]]}–${INICIAL_DIA[t[t.length - 1]]}` : INICIAL_DIA[t[0]])).join(', ');
    };
    return grupos.map(g => ({ dias: textoDias(g.dias), horas: g.horas }));
};

// ---------- Fichajes (Mi jornada y Mis fichajes) ----------

export const horaFichaje = (fecha: string | number | Date) => new Date(fecha).toLocaleTimeString('es-ES', { hour: 'numeric', minute: '2-digit' });

// «3 h 12 min», «45 min», «7 h»
export const duracionFichada = (ms: number) => {
    const minutos = Math.max(0, Math.floor(ms / 60000));
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
};

// «+15 min», «−1 h 30 min», «0 min»
export const diferenciaFichada = (ms: number) => {
    const minutos = ms >= 0 ? Math.floor(ms / 60000) : -Math.floor(-ms / 60000);
    if (minutos === 0) return '0 min';
    return `${minutos > 0 ? '+' : '−'}${duracionFichada(Math.abs(minutos) * 60000)}`;
};

// ---------- Correcciones del admin ----------

export const NOMBRE_FICHAJE: Record<TipoFichaje, string> = { entrada: 'Entrada', salida: 'Salida', inicioPausa: 'Inicio de pausa', finPausa: 'Fin de pausa' };
const porQuien = (creadoPor: number, nombres?: NombresCorrecciones, especialistaIdPersona?: number) =>
    creadoPor === especialistaIdPersona ? 'por la propia persona (admin)' : nombres?.[creadoPor] ? `por ${nombres[creadoPor]}` : 'por el administrador';
const conMotivo = (motivo?: string) => (motivo ? ` · ${motivo}` : '');

// «Corregido por Ana López · antes 9:20 · se le olvidó», «Hora confirmada por …», «Añadido por …». Null si no lo tocó el
// admin. Con la persona, se marca cuando el admin corrige lo suyo.
export const textoCorreccion = (e: IJornadaEvento, nombres?: NombresCorrecciones, especialistaIdPersona?: number): string | null => {
    const c = e.correccion;
    if (!c && e.origen === 'admin') return 'Corregido por el administrador';
    if (!c) return null;
    const quien = porQuien(c.creadoPor, nombres, especialistaIdPersona);
    if (c.tipo === 'anadida') return `Añadido ${quien}${conMotivo(c.motivo)}`;
    if (c.tipo === 'confirmada') return `Hora confirmada ${quien}${conMotivo(c.motivo)}`;
    return `Corregido ${quien} · antes ${c.original ? horaFichaje(c.original.hora) : '—'}${conMotivo(c.motivo)}`;
};
// «Entrada 9:00 · anulado por Ana López · era un error»
export const textoAnulado = (a: IFichajeAnulado, nombres?: NombresCorrecciones, especialistaIdPersona?: number) =>
    `${NOMBRE_FICHAJE[a.tipo]} ${horaFichaje(a.hora)} · anulado ${porQuien(a.creadoPor, nombres, especialistaIdPersona)}${conMotivo(a.motivo)}`;

// «Entrada 9:02», «Pausa 11:00–11:15», «Salida 14:00»: las pausas se juntan en un solo elemento. Se marca si alguna hora
// es declarada y se dan los textos de las correcciones del admin (para el tooltip).
export interface LineaFichajes { texto: string; declarada: boolean; correcciones: string[] }
export const resumenFichajes = (eventos: IJornadaEvento[], nombres?: NombresCorrecciones): LineaFichajes[] => {
    const lineas: LineaFichajes[] = [];
    let pausa: IJornadaEvento | null = null;
    const linea = (texto: string, ...lista: IJornadaEvento[]): LineaFichajes => ({
        texto,
        declarada: lista.some(e => e.origen === 'declarada'),
        correcciones: lista.map(e => textoCorreccion(e, nombres)).filter((t): t is string => !!t),
    });
    for (const e of eventos) {
        if (e.tipo === 'entrada') lineas.push(linea(`Entrada ${horaFichaje(e.hora)}`, e));
        else if (e.tipo === 'inicioPausa') pausa = e;
        else if (e.tipo === 'finPausa' && pausa) { lineas.push(linea(`Pausa ${horaFichaje(pausa.hora)}–${horaFichaje(e.hora)}`, pausa, e)); pausa = null; }
        else if (e.tipo === 'salida') {
            if (pausa) { lineas.push(linea(`Pausa ${horaFichaje(pausa.hora)}–${horaFichaje(e.hora)}`, pausa, e)); pausa = null; }
            lineas.push(linea(`Salida ${horaFichaje(e.hora)}`, e));
        }
    }
    if (pausa) lineas.push(linea(`Pausa desde las ${horaFichaje(pausa.hora)}`, pausa));
    return lineas;
};

const dosDigitos = (n: number) => String(n).padStart(2, '0');
// Una fecha a 'HH:mm', en la hora del dispositivo
export const aHHmm = (fecha: Date) => `${dosDigitos(fecha.getHours())}:${dosDigitos(fecha.getMinutes())}`;
// Un día ('YYYY-MM-DD' o el de una fecha) a una hora 'HH:mm', en la hora del dispositivo (como el resto de la pantalla)
export const conHora = (dia: Date | string, hhmm: string) => {
    const base = typeof dia === 'string' ? new Date(Number(dia.slice(0, 4)), Number(dia.slice(5, 7)) - 1, Number(dia.slice(8, 10))) : new Date(dia);
    base.setHours(Number(hhmm.slice(0, 2)), Number(hhmm.slice(3, 5)), 0, 0);
    return base;
};
export const HORA_VALIDA = /^([01]\d|2[0-3]):[0-5]\d$/;

// 'YYYY-MM-DD' ± días, sin zona (las fechas de la jornada son días)
export const sumarDias = (fecha: string, dias: number) => {
    const d = new Date(Number(fecha.slice(0, 4)), Number(fecha.slice(5, 7)) - 1, Number(fecha.slice(8, 10)) + dias);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Lunes de la semana de una fecha
export const lunesDe = (fecha: string) => {
    const d = new Date(Number(fecha.slice(0, 4)), Number(fecha.slice(5, 7)) - 1, Number(fecha.slice(8, 10)));
    return sumarDias(fecha, -((d.getDay() + 6) % 7));
};
// «Lunes 10», «martes 11 nov»
export const diaSemanaCorto = (fecha: string, conMes = false) => {
    const d = new Date(Number(fecha.slice(0, 4)), Number(fecha.slice(5, 7)) - 1, Number(fecha.slice(8, 10)));
    const texto = d.toLocaleDateString('es-ES', conMes ? { weekday: 'long', day: 'numeric', month: 'short' } : { weekday: 'long', day: 'numeric' }).replace(',', '').replace('.', '');
    return texto.charAt(0).toUpperCase() + texto.slice(1);
};

// ---------- Meses ('YYYY-MM') ----------

export const mesDe = (fecha: string) => fecha.slice(0, 7);
export const primerDia = (mes: string) => `${mes}-01`;
export const siguienteMes = (mes: string) => {
    const [a, m] = mes.split('-').map(Number);
    return m === 12 ? `${a + 1}-01` : `${a}-${String(m + 1).padStart(2, '0')}`;
};
export const mesAnterior = (mes: string) => {
    const [a, m] = mes.split('-').map(Number);
    return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
};
export const ultimoDia = (mes: string) => sumarDias(primerDia(siguienteMes(mes)), -1);
// «Octubre de 2026»
export const nombreMes = (mes: string) => {
    const texto = new Date(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)) - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
    return texto.charAt(0).toUpperCase() + texto.slice(1);
};
